import { dateToLocaleString } from '@/utilities/dateToLocaleString'

import type { Bill } from '@/payload-types'

export type BillProgressTimelineEntry = {
  label: string
  completed: boolean
  date: string | null
  status?: 'failed' | null
}

const ENACTED_PROGRESS_LABEL = 'Signed/Enacted/Adopted'
const VETOED_PROGRESS_LABEL = 'Vetoed'
const DEAD_PROGRESS_LABEL = 'Dead'

const FAILED_BILL_PROGRESS_STATUSES: readonly string[] = [
  DEAD_PROGRESS_LABEL,
  VETOED_PROGRESS_LABEL,
]

const TERMINAL_BILL_PROGRESS_STATUSES: readonly string[] = [
  ...FAILED_BILL_PROGRESS_STATUSES,
  ENACTED_PROGRESS_LABEL,
]

function isFailedBillProgress(billProgress: string): boolean {
  return FAILED_BILL_PROGRESS_STATUSES.includes(billProgress)
}

/**
 * Whether BillTrack50 considers the bill finished for progress UI purposes.
 */
export function isTerminalBillProgress(billProgress?: string | null): boolean {
  if (!billProgress) {
    return false
  }

  return TERMINAL_BILL_PROGRESS_STATUSES.includes(billProgress)
}

/**
 * Short label for the public progress UI (admin keeps the full BillTrack50 label).
 */
export function displayProgressStepLabel(label: string): string {
  if (label === ENACTED_PROGRESS_LABEL) {
    return 'Enacted'
  }
  return label
}

/**
 * Status line for bill cards: either BillTrack50 `billProgress` + `actionDate` or scraped timeline.
 */
export function getBillCardStatusDisplay(
  bill: Bill,
  options?: { useBillProgress?: boolean },
): { dateLabel: string; statusLabel: string } {
  if (options?.useBillProgress) {
    const progress = bill.bt50?.billProgress?.trim()
    const actionDate = bill.bt50?.actionDate
    return {
      statusLabel: displayProgressStepLabel(progress || 'Pending'),
      dateLabel: actionDate ? dateToLocaleString(actionDate) : 'Pending',
    }
  }

  const billStatus = getNewestBillStatus(bill)
  return {
    statusLabel: displayProgressStepLabel(billStatus?.label ?? 'Pending'),
    dateLabel: billStatus?.date ? dateToLocaleString(billStatus.date) : 'Pending',
  }
}

function billProgressValue(bill: Bill): string | undefined {
  const value = bill.bt50?.billProgress
  return typeof value === 'string' ? value : undefined
}

function progressSteps(bill: Bill): Array<{ label: string; date: string }> {
  const steps = bill.billtrackSite?.progressSteps
  if (!Array.isArray(steps)) {
    return []
  }

  return steps.filter(
    (step): step is { label: string; date: string } =>
      typeof step?.label === 'string' &&
      step.label.length > 0 &&
      typeof step?.date === 'string' &&
      step.date.length > 0,
  )
}

/**
 * Builds the bill progress timeline for UI from scraped steps and `bt50.billProgress`.
 * Returns null when there are no dated scraped steps.
 */
export function getBillProgressTimeline(bill: Bill): BillProgressTimelineEntry[] | null {
  const steps = progressSteps(bill)
  if (steps.length === 0) {
    return null
  }

  const billProgress = billProgressValue(bill)
  const timeline: BillProgressTimelineEntry[] = steps.map((step) => ({
    label: step.label,
    date: step.date,
    completed: true,
    status: null,
  }))

  if (billProgress && isFailedBillProgress(billProgress)) {
    const last = timeline[timeline.length - 1]
    if (last) {
      last.status = 'failed'
    }
    return timeline
  }

  if (billProgress === ENACTED_PROGRESS_LABEL) {
    return timeline
  }

  timeline.push({
    label: 'Pending',
    completed: false,
    date: null,
    status: null,
  })

  return timeline
}

/**
 * Returns the newest status entry for bill cards, or null when no timeline exists.
 */
export function getNewestBillStatus(bill: Bill): BillProgressTimelineEntry | null {
  const timeline = getBillProgressTimeline(bill)
  if (!timeline?.length) {
    return null
  }

  const incomplete = timeline.find((entry) => !entry.completed)
  if (incomplete) {
    return incomplete
  }

  return timeline[timeline.length - 1] ?? null
}
